import isObject from 'lodash/isObject';
import { AuthErrorCode, Route } from 'src/constants';
import { transformFieldsToCamelCase } from 'src/helpers/utils';
import { onUnauthorized } from 'src/hooks/data/users';

export enum HttpMethod {
  POST = 'POST',
  GET = 'GET',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
  PUT = 'PUT',
}

const getRequestHeaders = (method: HttpMethod): Record<string, string> => {
  if ([HttpMethod.POST, HttpMethod.PUT, HttpMethod.PATCH].includes(method)) {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
  }

  return {};
};

export interface FetchMethodParams {
  url: string;
  method?: HttpMethod;
  headers?: HeadersInit;
  data?: unknown;
  // errorMsg?: string;
  isTransformToCamelCase?: boolean;
  signal?: AbortSignal;
  cache?: RequestCache;
  isBlob?: boolean;
}

export interface FetchError {
  status: number;
  code: number;
  message?: string | Record<string, string>;
  meta?: Record<string, string>;
  msg?: string;
  error?: string;
  detail?: string;
}

export interface RefreshTokenResponse {
  access: string;
  refresh: string;
}

// @see https://www.freecodecamp.org/news/how-to-check-internet-connection-status-with-javascript/
// export const checkOnlineStatus = async () => {
//   try {
//     const online = await fetch('/assets/public/images/1x1.png');
//     return online.status >= 200 && online.status < 300; // either true or false
//   } catch (err) {
//     return false; // definitely offline
//   }
// };

// Overload
// @see https://www.typescriptlang.org/docs/handbook/2/functions.html#function-overloads
export async function getFetchMethod<Response>(data: FetchMethodParams): Promise<Response>;

export async function getFetchMethod<Response, Transformed>(
  data: FetchMethodParams,
  transform: (json: Response) => Transformed,
): Promise<Transformed>;

export async function getFetchMethod<Response, Transformed>(
  {
    url,
    method = HttpMethod.GET,
    headers,
    data = null,
    isTransformToCamelCase = false,
    signal,
    cache,
    isBlob = false,
  }: FetchMethodParams,
  transform?: (json: Response) => Transformed,
) {
  const res = await fetch(url, {
    method,
    headers: { ...getRequestHeaders(method), ...headers },
    body: (isObject(data) ? JSON.stringify(data) : data) as string,
    signal,
    credentials: 'include',
    cache,
  });

  // If it's ok then return the response
  if (res.ok) {
    if (isBlob) {
      const blob = await res.blob();

      return blob;
    }

    if (res.headers.get('Content-Type')?.includes('application/json')) {
      const json = await res.json();
      const camelCased = (isTransformToCamelCase ? transformFieldsToCamelCase(json) : json) as Response;

      return transform ? transform(camelCased) : camelCased;
    }

    const text = await res.text();

    return text;
  }

  const json = await res.json();
  const error: FetchError = {
    status: res.status,
    ...json,
  };

  if (error.message === AuthErrorCode.UNAUTHORIZED) {
    onUnauthorized(window.location.pathname !== Route.SIGN_IN);
  }

  return Promise.reject(error);
}
